/** NOTE: This component is shared with MKP!
 * Prevent regressions by spoofing as a tenant when testing. */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { extractTenantId } from '@kentik/ui-shared/alerting/applicationUtils';
import { Flex, Heading, Link, Icon, Text, Callout, Box } from 'core/components';
import LabelValue from 'app/views/metrics/components/LabelValue';
import { fromNow } from 'core/util/dateUtils';
import CommentList from 'app/components/comments/CommentList';
import PolicyLink from 'app/components/links/PolicyLink';
import TenantLink from 'app/components/links/TenantLink';
import { AlertAckChip } from '../AlertAckChip';
import AlertSeverityHistory from '../AlertSeverityHistory';

import AlertingSidebarItem from './AlertingSidebarItem';
import MitigationDetails from '../MitigationDetails';
import AlertOverview from './AlertOverview';
import AlertActions from './AlertActions';
import { renderExternalContextItems, renderAiInvestigationItems } from '../externalContexts';

@inject('$app', '$mkp')
@withRouter
@observer
class AlertingSidebar extends Component {
  get tenantModel() {
    const { $mkp, model } = this.props;
    return $mkp.tenants.get(extractTenantId(model.application));
  }

  handleViewPolicyAlerts = () => {
    const { policy, history } = this.props;

    if (policy.ruleId) {
      const filters = {
        ruleIds: [policy.ruleId],
        lookback: 3600 * 24 * 7, // 7 day lookback
        states: ['clear', 'alarm']
      };

      history.push('/v4/alerting/', { filters });
    }
  };

  render() {
    const { $app, model, device, policy, policyAlarmCount } = this.props;

    const policyId = !Number.isNaN(policy.id) && policy.id;
    const aiInvestigationItems = renderAiInvestigationItems(model.externalContexts, { labelWidth: 125 });

    return (
      <Flex flexDirection="column" width={400} m={0} p={3} pr={0} borderLeft="thin">
        {model.isAcked && (
          <Box mb={2}>
            <AlertAckChip model={model} />
          </Box>
        )}

        {!$app.isSubtenant && this.tenantModel && (
          <AlertingSidebarItem mb={2} icon="key" iconProps={{ ml: '2px', iconSize: 16 }} label="Tenant">
            <TenantLink id={this.tenantModel.id} alignText="left" label={this.tenantModel.get('name')} />
          </AlertingSidebarItem>
        )}

        <Flex flexDirection="column" gap={3} flex={1}>
          <Flex flexDirection="column" gap={1}>
            <Flex alignItems="center" gap={2}>
              <Heading level={6} m={0}>
                Alert Overview
              </Heading>
            </Flex>
            <AlertOverview alertModel={model} labelWidth={125} />
            {model.mitigationID && (
              <AlertingSidebarItem
                mt={2}
                label="Mitigation Details"
                value={<MitigationDetails model={model} showHeading={false} />}
              />
            )}
            {renderExternalContextItems(model.externalContexts, { labelWidth: 125 })}
          </Flex>
          {aiInvestigationItems && (
            <Flex flexDirection="column" gap={1}>
              <Heading level={6} m={0}>
                AI Advisor Investigation
              </Heading>
              {aiInvestigationItems}
            </Flex>
          )}
          {device ? (
            <Flex flexDirection="column" gap={1}>
              <Flex alignItems="flex-end" gap={2}>
                <Heading level={6} m={0}>
                  Device
                </Heading>
                <Link small to={`/v4/nms/devices/${device.id}`}>
                  View Details
                </Link>
              </Flex>
              <LabelValue label="Name" value={device.get('name')} labelWidth={125} />
              {device.get('site.title') && (
                <LabelValue label="Site" value={device.get('site.title')} labelWidth={125} />
              )}
              <LabelValue label="IP Address" value={device.get('ip_address')} labelWidth={125} />
              <LabelValue label="Model" value={device.get('model')} labelWidth={125} />
              <LabelValue label="Manufacturer" value={device.get('vendor')} labelWidth={125} />
              {device.get('serial_number') && (
                <LabelValue label="Serial Number" value={device.get('serial_number')} labelWidth={125} />
              )}
            </Flex>
          ) : null}
          {policyId ? (
            <Flex flexDirection="column" gap={1}>
              <Flex alignItems="flex-end" gap={2}>
                <Heading level={6} m={0}>
                  Policy
                </Heading>
                {!$app.isSubtenant && (
                  <PolicyLink small id={policy.id}>
                    Edit Policy
                  </PolicyLink>
                )}
              </Flex>
              {model.policyChanged && (
                <Callout intent="warning" p="12px" my={1}>
                  <Flex gap={1}>
                    <Icon icon="warning-sign" color="warning" />
                    <Text as="div" fontSize={13} lineHeight="auto">
                      This policy has been changed. The threshold may have been modified since this alert started.
                    </Text>
                  </Flex>
                </Callout>
              )}
              <LabelValue label="Name" value={policy.get('name')} labelWidth={125} />
              <LabelValue
                label="Last Edited"
                value={fromNow(model.isNmsPolicy ? policy.versionCreatedAt : policy.get('lastEditTime'))}
                labelWidth={125}
              />
              <LabelValue
                label="Alerts (7 days)"
                value={
                  <Text
                    as="a"
                    color="darkGray5"
                    fontWeight="bold"
                    onClick={this.handleViewPolicyAlerts}
                    rightIcon="arrow-right"
                  >
                    {policyAlarmCount.toLocaleString()}
                    <Icon icon="arrow-right" iconSize={12} color="muted" ml="5px" mb="2px" />
                  </Text>
                }
                labelWidth={125}
              />
            </Flex>
          ) : (
            <Flex flexDirection="column" gap={1}>
              <Heading level={6} m={0} color="muted">
                Policy Not Found
              </Heading>
              <Text muted>The policy for this alert was deleted.</Text>
            </Flex>
          )}

          {model.severityHistory.length > 1 && (
            <Box>
              <Heading level={6} mb={1}>
                Severity History
              </Heading>
              <AlertSeverityHistory severityHistory={model.severityHistory} small />
            </Box>
          )}

          {!$app.isSubtenant && (
            <Box>
              <CommentList entityId={model.id} entityType="alert" model={model} addReply />
            </Box>
          )}

          <Flex flexDirection="column" gap={1}>
            <Heading level={6} m={0}>
              Take Action
            </Heading>
            <Box mx={-1}>
              <AlertActions
                alertModel={model}
                isSubtenant={$app.isSubtenant}
                tenant={this.tenantModel}
                showPolicyLink={false}
                showDetailsLink={false}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>
    );
  }
}

export default AlertingSidebar;
